import { type ActionEvent, Controller } from "@hotwired/stimulus";

/**
 * Quarterly subscription spend table controller.
 *
 * Provides three mutually exclusive view modes:
 *
 *  - **column** – money amounts, heatmap by column (compare subs per quarter).
 *  - **row** – money amounts, heatmap by row (compare quarters per sub).
 *  - **delta-row** – delta %, heatmap by row (compare % change per sub).
 *
 * Each ``<td>`` carries both values as ``data-spend-money-value`` and
 * ``data-spend-delta-value`` attributes, with two child ``<span>``
 * elements (``spend-mode-money`` / ``spend-mode-delta``) that are
 * toggled visible.  The active numeric value is written to
 * ``data-heatmap-value`` for sorting and heatmap coloring.
 *
 * Also handles:
 *
 * - **Sticky thead** – IntersectionObserver-based fixed header on scroll.
 * - **Sorting** – click-to-sort columns with cached cell values.
 * - **Heatmap** – percentile-bucketed cell coloring by column or row.
 */
export default class QuarterlySpendController extends Controller {
  static override values = {
    mode: { type: String, default: "column" },
  };
  static override targets = ["columnBtn", "rowBtn", "deltaRowBtn"];

  declare modeValue: "column" | "row" | "delta-row" | "";
  declare readonly hasColumnBtnTarget: boolean;
  declare readonly columnBtnTarget: HTMLElement;
  declare readonly hasRowBtnTarget: boolean;
  declare readonly rowBtnTarget: HTMLElement;
  declare readonly hasDeltaRowBtnTarget: boolean;
  declare readonly deltaRowBtnTarget: HTMLElement;

  // ── Sticky state ──────────────────────────────────────────────────
  private observer: IntersectionObserver | null = null;
  private sentinel: HTMLElement | null = null;
  private clone: HTMLElement | null = null;

  // ── Sort state ────────────────────────────────────────────────────
  private sortCol = -1;
  private sortAsc = true;

  /**
   * Pre-parsed row cache.  Built on connect and rebuilt when the view
   * mode changes.  Each value is either a number (for numeric cells)
   * or a lowercase string (for text cells).  Empty cells store `null`
   * and always sort last regardless of direction.
   */
  private rows: {
    el: HTMLTableRowElement;
    vals: (number | string | null)[];
  }[] = [];

  // ── Bound handlers ────────────────────────────────────────────────
  private boundSyncScroll = this.syncScroll.bind(this);
  private boundCheckBottom = this.checkBottom.bind(this);
  private boundUpdateRect = this.updateRect.bind(this);
  private boundHeaderClick = this.onHeaderClick.bind(this);

  // ── Element accessors ─────────────────────────────────────────────

  /** The scrollable area containing the table. */
  private get area(): HTMLElement {
    return (
      this.element.querySelector<HTMLElement>(".overflow-x-auto") ??
      (this.element as HTMLElement)
    );
  }

  private get table(): HTMLTableElement | null {
    return this.area.querySelector("table");
  }

  private get thead(): HTMLTableSectionElement | null {
    return this.table?.querySelector("thead") ?? null;
  }

  private get tbody(): HTMLTableSectionElement | null {
    return this.table?.querySelector("tbody") ?? null;
  }

  /** Map the view mode to the heatmap coloring axis. */
  private get heatmapAxis(): "" | "column" | "row" {
    if (this.modeValue === "column") return "column";
    if (this.modeValue) return "row";
    return "";
  }

  // ═══════════════════════════════════════════════════════════════════
  // Lifecycle
  // ═══════════════════════════════════════════════════════════════════

  override connect(): void {
    this.buildCache();
    this.initSticky();
    this.thead?.addEventListener("click", this.boundHeaderClick);

    // Show the most recent quarters (rightmost columns) on load.
    this.area.scrollLeft = this.area.scrollWidth;
  }

  override disconnect(): void {
    this.observer?.disconnect();
    this.sentinel?.remove();
    this.sentinel = null;
    this.thead?.removeEventListener("click", this.boundHeaderClick);
    this.unpin();
  }

  // ═══════════════════════════════════════════════════════════════════
  // View mode
  // ═══════════════════════════════════════════════════════════════════

  /** Stimulus action wired to the view-mode buttons. */
  setMode(event: ActionEvent): void {
    const mode = (event.params as { mode: string }).mode;
    // Clicking the already-active button toggles heatmap off.
    this.modeValue =
      this.modeValue === mode ? "" : (mode as "column" | "row" | "delta-row");
  }

  /** Stimulus value callback — fires whenever modeValue changes. */
  modeValueChanged(): void {
    this.updateButtons();
    // Cell content must update before the cache rebuild so that
    // data-heatmap-value reflects the newly visible values.
    this.updateCells();
    this.buildCache();
    this.applyHeatmap();
  }

  // ── Button state ────────────────────────────────────────────────

  private updateButtons(): void {
    const setActive = (el: HTMLElement, active: boolean): void => {
      el.classList.toggle("bg-gray-100", active);
      el.classList.toggle("text-gray-900", active);
      el.classList.toggle("bg-white", !active);
      el.classList.toggle("text-gray-500", !active);

      const label = el.querySelector<HTMLElement>(".heatmap-label");
      if (label) label.hidden = !active;
    };
    if (this.hasColumnBtnTarget) {
      setActive(this.columnBtnTarget, this.modeValue === "column");
      this.columnBtnTarget.setAttribute(
        "aria-pressed",
        String(this.modeValue === "column"),
      );
    }
    if (this.hasRowBtnTarget) {
      setActive(this.rowBtnTarget, this.modeValue === "row");
      this.rowBtnTarget.setAttribute(
        "aria-pressed",
        String(this.modeValue === "row"),
      );
    }
    if (this.hasDeltaRowBtnTarget) {
      setActive(this.deltaRowBtnTarget, this.modeValue === "delta-row");
      this.deltaRowBtnTarget.setAttribute(
        "aria-pressed",
        String(this.modeValue === "delta-row"),
      );
    }
  }

  // ── Cell content switching ──────────────────────────────────────

  /**
   * Toggle between money and delta display.
   *
   * Each dual-mode cell has two ``<span>`` children — one for the
   * money amount and one for the delta percentage.  This method shows
   * the right one and writes the active numeric value to
   * ``data-heatmap-value`` so sorting and heatmap coloring use the
   * correct figure.
   */
  private updateCells(): void {
    const showMoney = this.modeValue !== "delta-row";
    const tbody = this.tbody;
    if (!tbody) return;

    for (const cell of tbody.querySelectorAll<HTMLTableCellElement>(
      "td[data-spend-money-value]",
    )) {
      const moneySpan = cell.querySelector<HTMLElement>(".spend-mode-money");
      const deltaSpan = cell.querySelector<HTMLElement>(".spend-mode-delta");
      if (moneySpan) moneySpan.hidden = !showMoney;
      if (deltaSpan) deltaSpan.hidden = showMoney;

      const value = showMoney
        ? cell.dataset.spendMoneyValue
        : cell.dataset.spendDeltaValue;
      if (value !== undefined) {
        cell.dataset.heatmapValue = value;
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Sorting
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Parse every body-row cell once and cache the result.
   *
   * If a cell carries a ``data-heatmap-value`` attribute, that value is
   * used for both sorting and heatmap coloring.  Otherwise the visible
   * text content is parsed.
   */
  private buildCache(): void {
    const tbody = this.tbody;
    if (!tbody) return;

    this.rows = Array.from(tbody.rows, (row) => ({
      el: row,
      vals: Array.from(row.cells, (cell) => {
        // Prefer an explicit heatmap value when present.
        const explicit = cell.dataset.heatmapValue;
        if (explicit !== undefined) {
          const num = Number.parseFloat(explicit);
          return Number.isNaN(num) ? null : num;
        }

        const text = cell.textContent?.trim() ?? "";
        if (text === "") return null;
        // Strip thousand-separator commas and try numeric parse.
        const num = Number.parseFloat(text.replace(/,/g, ""));
        return Number.isNaN(num) ? text.toLowerCase() : num;
      }),
    }));
  }

  private onHeaderClick(e: Event): void {
    const th = (e.target as Element).closest("th");
    if (!th || !this.thead) return;

    const headers = this.thead.querySelectorAll("th");
    let col = -1;
    headers.forEach((h, i) => {
      if (h === th) col = i;
    });
    if (col < 0) return;

    this.sortByColumn(col);
  }

  private sortByColumn(col: number): void {
    if (col === this.sortCol) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortCol = col;
      this.sortAsc = true;
    }

    const dir = this.sortAsc ? 1 : -1;

    this.rows.sort((a, b) => {
      const av = a.vals[col];
      const bv = b.vals[col];

      // Nulls (empty cells) always sort last.
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;

      // Both numbers → numeric compare.
      if (typeof av === "number" && typeof bv === "number")
        return (av - bv) * dir;

      // Fallback to string compare.
      const sa = typeof av === "string" ? av : String(av);
      const sb = typeof bv === "string" ? bv : String(bv);
      return sa < sb ? -dir : sa > sb ? dir : 0;
    });

    // Single reflow: move existing row nodes via a fragment.
    const tbody = this.tbody;
    if (!tbody) return;
    const frag = document.createDocumentFragment();
    for (const row of this.rows) {
      frag.appendChild(row.el);
    }
    tbody.appendChild(frag);

    this.updateSortIcons();
    this.applyHeatmap();
  }

  /**
   * Swap the FA icon class on every sort indicator in both the original
   * thead and the sticky clone (if present).  We replace the element so
   * FA's MutationObserver re-renders the SVG.
   */
  private updateSortIcons(): void {
    const update = (header: HTMLTableCellElement, isActive: boolean): void => {
      const old = header.querySelector(".sort-icon");
      if (!old) return;

      const icon = document.createElement("i");
      icon.className = `sort-icon ml-1 text-gray-400 inline-block group-hover:text-gray-600 fa-solid ${
        isActive ? (this.sortAsc ? "fa-caret-up" : "fa-caret-down") : "fa-sort"
      }`;
      old.replaceWith(icon);

      header.setAttribute(
        "aria-sort",
        isActive ? (this.sortAsc ? "ascending" : "descending") : "none",
      );
    };

    // Original thead.
    this.thead?.querySelectorAll("th").forEach((th, i) => {
      update(th, i === this.sortCol);
    });

    // Cloned thead (if pinned).
    this.clone?.querySelectorAll("thead th").forEach((th, i) => {
      update(th as HTMLTableCellElement, i === this.sortCol);
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // Sticky thead
  // ═══════════════════════════════════════════════════════════════════

  private initSticky(): void {
    // A zero-height sentinel at the top of the scroll area.  When it
    // leaves the viewport the thead gets pinned.
    this.sentinel = document.createElement("div");
    this.sentinel.style.height = "0";
    this.area.prepend(this.sentinel);

    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (entry.isIntersecting) {
        this.unpin();
      } else {
        // Only pin when the table has scrolled *up* past the viewport,
        // not when it's still below the fold.
        if (this.area.getBoundingClientRect().top < 0) {
          this.pin();
        }
      }
    });
    this.observer.observe(this.sentinel);
  }

  private pin(): void {
    if (this.clone || !this.thead) return;

    const thead = this.thead;
    const rect = this.area.getBoundingClientRect();

    // Snapshot column widths so the clone matches exactly.
    const ths = thead.querySelectorAll("th");
    const widths = new Float64Array(ths.length);
    ths.forEach((th, i) => {
      widths[i] = th.getBoundingClientRect().width;
    });

    const clonedThead = thead.cloneNode(true) as HTMLTableSectionElement;
    const clonedThs = clonedThead.querySelectorAll("th");
    clonedThs.forEach((th, i) => {
      th.style.width = `${widths[i]}px`;
      th.style.minWidth = `${widths[i]}px`;
    });

    // Forward clicks on cloned headers to the sort logic.
    clonedThs.forEach((th, i) => {
      th.style.cursor = "pointer";
      th.addEventListener("click", (e) => {
        e.stopPropagation();
        this.sortByColumn(i);
      });
    });

    const table = document.createElement("table");
    table.className = this.table?.className ?? "";
    table.appendChild(clonedThead);

    const wrapper = document.createElement("div");
    wrapper.style.cssText = [
      "position:fixed",
      "top:0",
      `left:${rect.left}px`,
      `width:${rect.width}px`,
      "overflow:hidden",
      "z-index:40",
      "box-shadow:0 2px 8px rgba(0,0,0,.08)",
    ].join(";");
    wrapper.appendChild(table);

    table.style.marginLeft = `-${this.area.scrollLeft}px`;

    document.body.appendChild(wrapper);
    this.clone = wrapper;

    this.area.addEventListener("scroll", this.boundSyncScroll);
    window.addEventListener("scroll", this.boundCheckBottom, {
      passive: true,
    });
    window.addEventListener("resize", this.boundUpdateRect, { passive: true });
  }

  private unpin(): void {
    if (!this.clone) return;
    this.area.removeEventListener("scroll", this.boundSyncScroll);
    window.removeEventListener("scroll", this.boundCheckBottom);
    window.removeEventListener("resize", this.boundUpdateRect);
    this.clone.remove();
    this.clone = null;
  }

  private syncScroll(): void {
    const table = this.clone?.querySelector("table");
    if (table) table.style.marginLeft = `-${this.area.scrollLeft}px`;
  }

  private updateRect(): void {
    if (!this.clone) return;
    const rect = this.area.getBoundingClientRect();
    this.clone.style.left = `${rect.left}px`;
    this.clone.style.width = `${rect.width}px`;
  }

  private checkBottom(): void {
    if (!this.clone) return;
    if (this.area.getBoundingClientRect().bottom < 0) this.unpin();
  }

  // ═══════════════════════════════════════════════════════════════════
  // Heatmap
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Color `<td>` cells by relative magnitude.
   *
   * In "column" mode each column is normalized independently — the
   * darkest cell is the highest value in that quarter.  In "row" mode
   * each row is normalized — the darkest cell is the highest-spend
   * quarter for that subscription.
   *
   * Cells with `data-heatmap-exclude` (e.g. the "Total" column) are
   * skipped from both normalization and coloring.
   */
  private applyHeatmap(): void {
    const tbody = this.tbody;
    if (!tbody) return;

    // Clear all existing heatmap styles first.
    for (const row of this.rows) {
      for (const cell of Array.from(row.el.cells)) {
        if (cell.tagName === "TD") {
          cell.style.backgroundColor = "";
        }
      }
    }

    const axis = this.heatmapAxis;
    if (axis === "column") {
      this.applyColumnHeatmap();
    } else if (axis === "row") {
      this.applyRowHeatmap();
    }
  }

  /** Check whether a cell is opted out of heatmap coloring. */
  private isExcluded(cell: HTMLTableCellElement): boolean {
    return cell.dataset.heatmapExclude !== undefined;
  }

  // ── Percentile-bucketed coloring ──────────────────────────────────
  //
  // Instead of linear min-max normalization (where a single extreme
  // outlier washes out every other cell), values are ranked and split
  // into equal-sized percentile buckets.  Each bucket receives a
  // fixed alpha intensity, guaranteeing visual contrast across the
  // full range of data.
  //
  // When values span both negative and positive (e.g. % change),
  // a diverging scheme is used: decreases in blue, increases in red.

  private static readonly BUCKET_ALPHAS = [0.08, 0.2, 0.35, 0.55, 0.75];
  private static readonly HUE_WARM = 25; // red  — spend increases
  private static readonly HUE_COOL = 250; // blue — spend decreases

  /**
   * Assign a heatmap color to each cell based on its percentile rank
   * within the group.
   *
   * Automatically switches to a diverging blue/red scheme when the
   * group contains both negative and positive values.
   */
  private colorByBucket(
    cells: { cell: HTMLTableCellElement; val: number }[],
  ): void {
    if (cells.length === 0) return;

    const hasNeg = cells.some((c) => c.val < 0);
    const hasPos = cells.some((c) => c.val > 0);

    if (hasNeg && hasPos) {
      // Diverging: rank each sign group by magnitude independently.
      const neg = cells.filter((c) => c.val < 0);
      const pos = cells.filter((c) => c.val > 0);
      this.colorGroup(neg, QuarterlySpendController.HUE_COOL);
      this.colorGroup(pos, QuarterlySpendController.HUE_WARM);
      // Zero values get no color.
      for (const { cell, val } of cells) {
        if (val === 0) cell.style.backgroundColor = "";
      }
    } else {
      // Single-sign: warm red for positive, cool blue for negative.
      const hue = hasNeg
        ? QuarterlySpendController.HUE_COOL
        : QuarterlySpendController.HUE_WARM;
      this.colorGroup(cells, hue);
    }
  }

  /**
   * Rank cells by absolute magnitude and apply bucketed coloring
   * with the given oklch hue.
   */
  private colorGroup(
    cells: { cell: HTMLTableCellElement; val: number }[],
    hue: number,
  ): void {
    const n = cells.length;
    if (n === 0) return;

    const alphas = QuarterlySpendController.BUCKET_ALPHAS;
    const k = alphas.length;

    const first = cells[0];
    if (!first || cells.every((c) => c.val === first.val)) {
      const mid = alphas[Math.floor(k / 2)] ?? 0.35;
      for (const { cell } of cells) {
        cell.style.backgroundColor = `oklch(0.65 0.18 ${hue} / ${mid.toFixed(3)})`;
      }
      return;
    }

    // Sort by absolute magnitude — biggest change gets darkest color.
    const ranked = cells
      .map((c, i) => ({ absVal: Math.abs(c.val), idx: i }))
      .sort((a, b) => a.absVal - b.absVal);

    for (let r = 0; r < n; r++) {
      const bucket = Math.min(Math.floor((r / n) * k), k - 1);
      const alpha = alphas[bucket] ?? 0.35;
      const entry = ranked[r];
      const target = entry ? cells[entry.idx] : undefined;
      if (target) {
        target.cell.style.backgroundColor = `oklch(0.65 0.18 ${hue} / ${alpha.toFixed(3)})`;
      }
    }
  }

  private applyColumnHeatmap(): void {
    const first = this.rows[0];
    if (!first) return;
    const numCols = first.el.cells.length;

    for (let col = 0; col < numCols; col++) {
      const cells: { cell: HTMLTableCellElement; val: number }[] = [];
      for (const row of this.rows) {
        const cell = row.el.cells[col];
        if (!cell || cell.tagName !== "TD" || this.isExcluded(cell)) continue;
        const val = row.vals[col];
        if (typeof val === "number") cells.push({ cell, val });
      }
      this.colorByBucket(cells);
    }
  }

  private applyRowHeatmap(): void {
    for (const row of this.rows) {
      const cells: { cell: HTMLTableCellElement; val: number }[] = [];
      for (let col = 0; col < row.el.cells.length; col++) {
        const cell = row.el.cells[col];
        if (!cell || cell.tagName !== "TD" || this.isExcluded(cell)) continue;
        const val = row.vals[col];
        if (typeof val === "number") cells.push({ cell, val });
      }
      this.colorByBucket(cells);
    }
  }
}
